import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ShieldCheck, Clock, UserCheck, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trust = [
  { icon: ShieldCheck, text: "100 % DSGVO-konform" },
  { icon: Clock, text: "Antwort innerhalb von 24 Stunden" },
  { icon: UserCheck, text: "Persönliche Betreuung, kein Callcenter" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: form.message,
      });
      setSent(true);
      toast.success("Anfrage gesendet! Du hörst innerhalb von 24 Stunden von mir.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Etwas ist schiefgelaufen. Bitte versuche es erneut oder schreib direkt an stephan@sa-webdesign.net.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white/[0.06] border border-white/15 px-4 py-3.5 text-white placeholder:text-white/35 focus:border-[#2563EB] focus:bg-white/[0.09] transition-[border-color,background-color] duration-200 outline-none text-[15px]";

  return (
    <section id="kontakt" className="py-20 md:py-32 bg-[#0A0A0A] text-white relative grain">
      <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-xs font-medium text-[#60A5FA]">08</span>
            <span className="h-px w-8 bg-[#60A5FA]" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/50">Kontakt</span>
          </div>
          <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl tracking-tight">
            Sichere dir dein kostenloses Erstgespräch.
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed max-w-md">
            Erzähl mir kurz von deinem Betrieb — ich melde mich innerhalb von 24 Stunden mit
            einer ehrlichen Einschätzung und einem konkreten Vorschlag.
          </p>
          <div className="mt-8 rounded-xl border border-[#2563EB]/30 bg-[#2563EB]/10 px-5 py-4 max-w-md">
            <p className="text-sm text-white/85 leading-relaxed">
              <span className="font-semibold text-white">Begrenzte Kapazität:</span> Damit jede
              Website die Aufmerksamkeit bekommt, die sie verdient, betreue ich maximal{" "}
              <span className="font-mono text-[#60A5FA]">3 neue Projekte pro Monat</span>.
            </p>
          </div>
          <ul className="mt-8 space-y-4">
            {trust.map((t) => (
              <li key={t.text} className="flex items-center gap-3 text-sm text-white/75">
                <t.icon className="w-5 h-5 text-[#60A5FA]" aria-hidden="true" />
                {t.text}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          {sent ? (
            <div
              data-testid="contact-success"
              className="h-full min-h-[380px] rounded-2xl border border-[#2563EB]/40 bg-[#2563EB]/10 flex flex-col items-center justify-center text-center p-10"
            >
              <div className="w-14 h-14 rounded-full bg-[#2563EB] flex items-center justify-center mb-6">
                <Send className="w-6 h-6 text-white" aria-hidden="true" />
              </div>
              <h3 className="font-display font-semibold text-2xl tracking-tight">Anfrage erhalten!</h3>
              <p className="mt-3 text-white/65 leading-relaxed max-w-sm">
                Danke, {form.name.split(" ")[0]}. Ich melde mich innerhalb von 24 Stunden bei dir —
                versprochen.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="c-name" className="block font-mono text-[11px] uppercase tracking-wider text-white/50 mb-2">
                    Name *
                  </label>
                  <input
                    id="c-name"
                    data-testid="contact-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Max Mustermann"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="c-phone" className="block font-mono text-[11px] uppercase tracking-wider text-white/50 mb-2">
                    Telefon (optional)
                  </label>
                  <input
                    id="c-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="0160 1234567"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="c-email" className="block font-mono text-[11px] uppercase tracking-wider text-white/50 mb-2">
                  E-Mail *
                </label>
                <input
                  id="c-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="max@deine-firma.de"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="c-message" className="block font-mono text-[11px] uppercase tracking-wider text-white/50 mb-2">
                  Worum geht's? *
                </label>
                <textarea
                  id="c-message"
                  data-testid="contact-message-input"
                  required
                  minLength={5}
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="z. B.: Ich bin Friseurmeister in Ratingen und brauche eine neue Website mit Online-Terminbuchung …"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer group" htmlFor="c-privacy">
                <input
                  id="c-privacy"
                  data-testid="contact-privacy-checkbox"
                  type="checkbox"
                  required
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 rounded border-white/25 bg-white/[0.06] accent-[#2563EB] cursor-pointer"
                />
                <span className="text-xs text-white/55 leading-relaxed group-hover:text-white/75 transition-colors duration-200">
                  Ich habe die{" "}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#60A5FA] underline hover:text-white">
                    Datenschutzerklärung
                  </a>{" "}
                  gelesen und bin mit der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage
                  einverstanden. *
                </span>
              </label>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] text-white font-semibold py-4 hover:bg-[#1D4ED8] disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Wird gesendet …
                  </>
                ) : (
                  "Kostenloses Erstgespräch sichern"
                )}
              </button>
              <p className="text-xs text-white/40 text-center leading-relaxed">
                Deine Daten werden nur zur Bearbeitung deiner Anfrage verwendet. Kein Newsletter,
                keine Weitergabe.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
